import React, {useCallback, useEffect, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {KTIcon} from '../../../_metronic/helpers'
import {restRequest} from '../../../api-utils/apiQueries'
import {convertToHours, formatDateToDDMMYYYY} from '../../common-function/formatDateToYearMonth'
import GiftCardFilter from './GiftCardFilter'
import Pagination from '../../common-function/Pagination'
import {ExportReportPayload, useExportReport} from '../../common-function/downloadExportFile'
import {
  appendDateFilterToBody,
  appendDateFilterToExport,
} from '../../common-function/adminDateFilterPayload'
import {useAdminListState} from '../../common-function/useAdminListState'
import ListFilterToolbar from '../../common-function/ListFilterToolbar'
import ListPageHeader from '../../common-function/ListPageHeader'
import {
  buildGiftCardFilterSummary,
  getDefaultGiftCardFilter,
  GiftCardFilterState,
} from './giftCardDateRange'
import GiftCardCategoryBadges from './GiftCardCategoryBadges'
import {parseGiftCardData} from './giftCardHelpers'

const LIST_STORAGE_KEY = 'gift-card-management'

const GiftCardManagementWrapper = () => {
  const {runExport, exporting} = useExportReport()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [giftCards, setGiftCards] = useState<any[]>([])
  const [showFilter, setShowFilter] = useState<boolean>(false)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)

  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    activeFilter,
    applyFilter,
    resetToDefault,
    resetPageForSearch,
    isFilterActive,
  } = useAdminListState<GiftCardFilterState>({
    storageKey: LIST_STORAGE_KEY,
    getDefaultFilter: getDefaultGiftCardFilter,
    isDefaultFilter: (f, search) =>
      f.preset === 'today' && f.category.length === 0 && !search.trim(),
  })

  const fetchGiftCardData = useCallback(
    async (query: string) => {
      setIsLoading(true)
      const bodyData: Record<string, unknown> = {
        page,
        search: query,
        category: activeFilter.category,
      }
      appendDateFilterToBody(bodyData, activeFilter)

      try {
        const response: any = await restRequest.getGiftCardData(bodyData)
        const data = response?.data?.data
        setGiftCards(data?.data ?? [])
        setTotalPages(data?.pager?.totalPages ?? 0)
        setTotalRecords(data?.pager?.totalRecords ?? 0)
      } catch (error) {
        console.error('Failed to load gift card data:', error)
        setGiftCards([])
        setTotalPages(0)
        setTotalRecords(0)
      } finally {
        setIsLoading(false)
      }
    },
    [page, activeFilter]
  )

  useEffect(() => {
    fetchGiftCardData(searchQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value === '') {
      resetPageForSearch()
      fetchGiftCardData('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      resetPageForSearch()
      fetchGiftCardData(searchQuery)
    }
  }

  const submitSearch = () => {
    resetPageForSearch()
    fetchGiftCardData(searchQuery)
  }

  const handleExportGiftCards = () => {
    const payload: ExportReportPayload = {type: 'gift_card'}
    appendDateFilterToExport(payload, activeFilter)
    if (activeFilter.category.length) payload.category = activeFilter.category
    if (searchQuery.trim()) payload.search = searchQuery.trim()
    runExport(payload)
  }

  const filterSummary = buildGiftCardFilterSummary(
    activeFilter,
    searchQuery,
    totalRecords
  )

  return (
    <div>
      <p className='card-label fw-bold fs-3 mb-2'>Gift Card Management</p>

      <ListFilterToolbar
        filterSummary={filterSummary}
        showReset={isFilterActive}
        onReset={resetToDefault}
      />

      <div className='card'>
        <ListPageHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchKeyDown={handleKeyDown}
          onSearchSubmit={submitSearch}
          searchPlaceholder='Search gift cards by user name'
          isLoading={isLoading}
          isFilterActive={isFilterActive}
          onOpenFilter={() => setShowFilter(true)}
          onExport={handleExportGiftCards}
          exporting={exporting}
        />
        <div className='card-body py-3'>
          {isLoading ? (
            <div className='d-flex justify-content-center py-10'>
              <div className='spinner-border text-primary'></div>
            </div>
          ) : (
            <div className='table-responsive'>
              {giftCards.length > 0 ? (
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-150px'>User Name</th>
                      <th className='min-w-140px'>Gift Card Category</th>
                      <th className='min-w-120px'>Gift Card</th>
                      <th className='min-w-120px'>Gift Card Value</th>
                      <th className='min-w-160px'>Receiver Email</th>
                      <th className='min-w-120px'>Time Of Claim</th>
                      <th className='min-w-100px'>Date</th>
                      <th className='min-w-80px text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {giftCards.map((item: any) => {
                      const parsed = parseGiftCardData(item.gift_card_data)
                      const userName = item?.user?.name ?? '—'
                      const userId = item?.user?.id ?? item?.user_id
                      const claimDate = formatDateToDDMMYYYY(item.createdAt)

                      return (
                        <tr key={item.id}>
                          <td>
                            {userId ? (
                              <Link
                                to={`/user-management/${userId}`}
                                className='text-dark fw-bold text-hover-primary fs-6'
                              >
                                {userName}
                              </Link>
                            ) : (
                              <span className='text-dark fw-bold fs-6'>{userName}</span>
                            )}
                          </td>
                          <td>
                            <GiftCardCategoryBadges categories={parsed.categories} />
                          </td>
                          <td>
                            <span className='text-dark fw-bold fs-6'>{parsed.title}</span>
                          </td>
                          <td>
                            <span className='text-dark fw-bold fs-6'>
                              {item.gift_card_amount || parsed.amount}
                            </span>
                          </td>
                          <td className='text-dark fw-bold fs-6'>
                            {item.recipient_email || parsed.recipientEmail}
                          </td>
                          <td>
                            <span className='text-dark fw-bold fs-6'>
                              {item.createdAt ? convertToHours(item.createdAt) : '—'}
                            </span>
                          </td>
                          <td>
                            <span className='text-dark fw-bold fs-6'>{claimDate}</span>
                          </td>
                          <td className='text-end'>
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              title='View details'
                              onClick={() => navigate(`/gift-card-management/${item.id}`)}
                            >
                              <KTIcon iconName='eye' className='fs-1' />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              ) : (
                <p className='text-center fs-5 pt-3'>
                  No gift card redemptions found for this filter.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination totalPages={totalPages} page={page} setPage={setPage} />
      )}

      <GiftCardFilter
        showFilter={showFilter}
        toggle={() => setShowFilter(false)}
        activeFilter={activeFilter}
        onApply={applyFilter}
      />
    </div>
  )
}

export default GiftCardManagementWrapper
